import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import type { User } from '@verantos-dev/core-annotation-tool';

import { useGetProjectDetailsQuery } from '../../api/adminProjectsApiSlice';
import { useEditProject } from '../../context/EditProjectContext';
import type { ReallocationOperation } from '../../context/editProjectReducer';
import { Annotator, AllocationSummary, Concept } from '../../types/response';
import { BATCH_LABEL_IDS } from '../ReallocationWizard/types';

// MUI's default Dialog exit transition (theme.transitions.duration.leavingScreen)
// is 195ms; the scroll lock it holds isn't released until that finishes.
const DIALOG_CLOSE_TRANSITION_MS = 250;

interface UseProjectOverviewResults {
  projectName: string;
  conditionAreaName: string;
  healthSystemName: string;
  annotatorNames: string[];
  supervisorNames: string[];
  /** Persisted annotators/supervisors (id + label) — used by the editable variant. */
  annotators: User[];
  supervisors: User[];
  addedConcepts: Concept[];
  validationEncounterCount: number;
  testEncounterCount: number;
  trainingEncounterCount: number;
  isDataLoading: boolean;
  allocationSummary: AllocationSummary[];

  editMode: boolean;
  projectId: string;
  addedAnnotators: User[];
  addedSupervisors: User[];
  addAnnotator: (person: User) => void;
  removeAnnotator: (annotatorId: string) => void;
  addSupervisor: (person: User) => void;
  removeSupervisor: (supervisorId: string) => void;

  isReallocationWizardOpen: boolean;
  openReallocationWizard: () => void;
  closeReallocationWizard: () => void;
  handleConfirmReallocation: (operations: ReallocationOperation[]) => void;
  confirmedToast: string | null;
  closeConfirmedToast: () => void;
}

export const useProjectOverview = (): UseProjectOverviewResults => {
  const { projectId: routeProjectId } = useParams();
  const { formatMessage } = useIntl();
  const [isReallocationWizardOpen, setReallocationWizardOpen] = useState(false);
  const [confirmedToast, setConfirmedToast] = useState<string | null>(null);

  const {
    editMode,
    projectId,
    addedAnnotators,
    addedSupervisors,
    removedSupervisorIds,
    confirmedReallocations,
    addAnnotator,
    removeAnnotator,
    addSupervisor,
    removeSupervisor,
    commitReallocations,
  } = useEditProject();

  const {
    data: projectDetailsData,
    isLoading: isProjectDetailsLoading,
    isFetching: isProjectDetailsFetching,
  } = useGetProjectDetailsQuery(
    { projectId: routeProjectId as string },
    { skip: !routeProjectId || routeProjectId === '' }
  );

  const annotators = projectDetailsData?.data?.annotators ?? [];
  const rawSupervisors = projectDetailsData?.data?.supervisors ?? [];
  const removedSupervisorSet = new Set(removedSupervisorIds);
  const supervisors = rawSupervisors.filter((s: User) => !removedSupervisorSet.has(s.id));
  const annotatorNames = annotators.map((annotator: Annotator) => annotator.label);
  const supervisorNames = supervisors.map((supervisor: User) => supervisor.label);
  const addedConcepts = projectDetailsData?.data?.conceptGroups.map((concept) => ({
    conceptGroupId: concept.conceptGroupId,
    conceptGroupName: concept.conceptGroupName,
    requiredAttributes: concept.requiredAttributes,
  })) as Concept[];

  // Overlay newly-added annotators into the allocation summary with zero
  // allocations so they appear immediately (UI-only until Save).
  const baseAllocation = projectDetailsData?.data?.allocationData ?? [];
  const existingAllocationIds = new Set(baseAllocation.map((row) => row.annotatorId));
  const addedAllocationRows: AllocationSummary[] = addedAnnotators
    .filter((annotator) => !existingAllocationIds.has(annotator.id))
    .map((annotator) => ({
      annotatorId: annotator.id,
      annotatorName: annotator.label,
      trainingCount: 0,
      testCount: 0,
      validationCount: 0,
    }));

  // Apply confirmed reallocation moves so the table reflects staged (unsaved) changes.
  const batchCountKey: Record<
    string,
    keyof Pick<AllocationSummary, 'trainingCount' | 'testCount' | 'validationCount'>
  > = {
    training: 'trainingCount',
    test: 'testCount',
    validation: 'validationCount',
  };
  const allocationById = new Map<string, AllocationSummary>(
    [...baseAllocation, ...addedAllocationRows].map((row) => [row.annotatorId, { ...row }])
  );
  confirmedReallocations.forEach((op) => {
    const key = batchCountKey[op.batch];
    const from = allocationById.get(op.fromAnnotatorId);
    const to = allocationById.get(op.toAnnotatorId);
    if (from) from[key] -= op.count;
    if (to) to[key] += op.count;
  });
  const allocationSummary = Array.from(allocationById.values());

  const handleConfirmReallocation = (operations: ReallocationOperation[]) => {
    commitReallocations(operations);
    setReallocationWizardOpen(false);

    // The Dialog's own close transition still holds MUI's scroll lock at
    // the instant this handler runs, which silently swallows a same-tick
    // scroll — defer past it, then bring the Save/Cancel controls
    // (rendered above ProjectOverview, in EditProjectControls) into view.
    setTimeout(() => {
      // The shared Button component drops a raw `id` prop and only ever
      // sets `data-testid={`button-${id}`}` on the underlying DOM node.
      document
        .querySelector('[data-testid="button-save-edit-changes-button"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, DIALOG_CLOSE_TRANSITION_MS);

    const batchLabels = [...new Set(operations.map((op) => op.batch))].map((batch) =>
      formatMessage({ id: BATCH_LABEL_IDS[batch] })
    );
    if (batchLabels.length) {
      const messageKey =
        batchLabels.length > 1
          ? 'adminProjects.reallocation.confirmedToast'
          : 'adminProjects.reallocation.confirmedToastSingle';
      setConfirmedToast(formatMessage({ id: messageKey }, { batches: batchLabels.join(', ') }));
    }
  };

  return {
    projectName: projectDetailsData?.data?.basics?.projectName ?? '',
    conditionAreaName: projectDetailsData?.data?.basics?.conditionAreaName ?? '',
    healthSystemName: projectDetailsData?.data?.basics?.healthSystemName ?? '',
    annotatorNames: annotatorNames ?? [],
    supervisorNames: supervisorNames ?? [],
    annotators,
    supervisors,
    addedConcepts: addedConcepts as Concept[],
    validationEncounterCount: projectDetailsData?.data?.dataSplitSummary?.validationCount ?? 0,
    testEncounterCount: projectDetailsData?.data?.dataSplitSummary?.testCount ?? 0,
    trainingEncounterCount: projectDetailsData?.data?.dataSplitSummary?.trainingCount ?? 0,
    isDataLoading: isProjectDetailsLoading || isProjectDetailsFetching,
    allocationSummary,

    editMode,
    projectId,
    addedAnnotators,
    addedSupervisors,
    addAnnotator,
    removeAnnotator,
    addSupervisor,
    removeSupervisor,

    isReallocationWizardOpen,
    openReallocationWizard: () => setReallocationWizardOpen(true),
    closeReallocationWizard: () => setReallocationWizardOpen(false),
    handleConfirmReallocation,
    confirmedToast,
    closeConfirmedToast: () => setConfirmedToast(null),
  };
};
